import { useOwnershipLabels } from "../../OwnershipLabels";
import { ownershipKnownNodes, ownershipRowNodeIds, shareholderRowNodeType } from "../../../utils/editOwnershipPercentage";
import { ownershipTreeWithMobileNodes } from "../../../utils/mobileAssistantTree";
import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import KycSectionOwnership from "../common/KycSectionOwnership";
import UboIdentificationNotice from "../common/UboIdentificationNotice";
import KycCdbFormsPanel from "../common/KycCdbFormsPanel";
import { OwnershipChoiceDialog } from "../common/OwnershipChangeDialogs";
import useOwnershipRemoval from "../../hooks/useOwnershipRemoval";
import { allOwnershipParties } from "../../../utils/ownershipPartyRemoval";
import {
  labelForNodeHtml,
  OwnershipTreeUaeReadOnly,
  toMermaidHtmlLabel,
} from "../common/OwnershipTreeUae";
import {
  assessUbosFromTree,
  applyUboAssessmentResultToOwnership,
  attachArrangementParties,
  convertAssistantRootsToNodes,
  rowDateText,
} from "../../../utils/uboAssessment";
import iconProduction from "../../../images/icon-production.svg";
import iconHolding from "../../../images/icon-holding.svg";
import iconPic from "../../../images/icon-pic.svg";
import iconTrust from "../../../images/icon-trust2.svg";
import iconFoundation from "../../../images/icon-foundation.svg";
import iconQuoted from "../../../images/icon-quoted.svg";
import iconIndividual from "../../../images/avatar.svg";
import iconSettlor from "../../../images/settlor.svg";
import iconTrustee from "../../../images/handshake.svg";
import iconBeneficiary from "../../../images/beneficiary.svg";
import iconProtector from "../../../images/settlor.svg";

function clean(value) {
  return String(value || "").trim();
}

function cloneForDebug(value) {
  try {
    if (typeof structuredClone === "function") return structuredClone(value);
  } catch (_) {}
  try {
    return JSON.parse(JSON.stringify(value ?? null));
  } catch (_) {
    return value;
  }
}

function pathLabel(path) {
  return Array.isArray(path) ? path.join(".") : String(path || "");
}

function previewOwnershipAfterSetAt(currentOwnership, path, value) {
  if (!Array.isArray(path) || path[0] !== "ownership") {
    return currentOwnership;
  }

  const nextOwnership = cloneForDebug(currentOwnership || {});
  if (path.length === 1) return value;

  let ref = nextOwnership;
  for (let i = 1; i < path.length - 1; i += 1) {
    const key = path[i];
    if (!ref[key] || typeof ref[key] !== "object") ref[key] = {};
    ref = ref[key];
  }
  ref[path[path.length - 1]] = value;
  return nextOwnership;
}

function summarizeOwnershipRows(rows = []) {
  return (Array.isArray(rows) ? rows : []).map((row, idx) => ({
    idx,
    rowId: clean(row?._rowId),
    treeNodeId: clean(row?.treeNodeId || row?.subjectTreeNodeId),
    relatedShareholderRowId: clean(row?.relatedShareholderRowId),
    relatedShareholderNodeId: clean(row?.relatedShareholderNodeId),
    name: clean(row?.name),
    holderType: clean(row?.holderType || row?.shareholderEntityType),
    ownershipPercent: clean(row?.ownershipPercent),
    effectiveOwnershipPercent: clean(row?.effectiveOwnershipPercent),
    basisOfControl: clean(row?.basisOfControl),
  }));
}

function summarizeGraphNodes(nodes = []) {
  return (Array.isArray(nodes) ? nodes : []).map((node, idx) => ({
    idx,
    id: clean(node?.ID || node?.id),
    name: clean(node?.name),
    type: clean(node?.type),
    parentIDs: Array.isArray(node?.parentIDs)
      ? node.parentIDs.map((id) => clean(id)).join(",")
      : "",
    percent:
      node?.percent === undefined || node?.percent === null
        ? ""
        : String(node.percent),
  }));
}

function summarizeApiPayload(payload) {
  return {
    initialAHType: clean(payload?.initialAHType),
    nodeCount: Array.isArray(payload?.nodes) ? payload.nodes.length : 0,
    nodes: summarizeGraphNodes(payload?.nodes || []),
  };
}

function legalArrangementType(arrangement) {
  return clean(arrangement?.arrangementType) === "Foundation"
    ? "Foundation"
    : "Trust";
}

function legalArrangementRoleIcon(role) {
  const normalizedRole = clean(role).toLowerCase();

  if (normalizedRole === "settlor") return iconSettlor;
  if (normalizedRole === "trustee") return iconTrustee;
  if (normalizedRole === "beneficiary") return iconBeneficiary;
  if (normalizedRole === "protector") return iconProtector;
  if (normalizedRole === "founder") return iconSettlor;
  if (
    normalizedRole === "council member" ||
    normalizedRole === "council/board"
  ) {
    return iconTrustee;
  }
  if (normalizedRole === "guardian") return iconSettlor;

  return iconIndividual;
}

function legalArrangementParties(arrangement) {
  const parties = Array.isArray(arrangement?.parties)
    ? arrangement.parties
    : Array.isArray(arrangement?.trustParties)
    ? arrangement.trustParties
    : Array.isArray(arrangement?.foundationParties)
    ? arrangement.foundationParties
    : [];

  return parties
    .map((party, idx) => {
      const name = clean(party?.name);
      if (!name) return null;

      const role = clean(party?.role) || "Related individual";
      const kind = clean(party?.trusteeKind || party?.councilKind);
      const meta = [role, kind].filter(Boolean).join(" | ");

      return {
        id: clean(party?._rowId) || `${name}-${idx}`,
        name,
        meta,
        icon: legalArrangementRoleIcon(role),
      };
    })
    .filter(Boolean);
}

function findLegalArrangementForDetectedNode({ ownership, detectedId, node }) {
  const arrangements = Array.isArray(ownership?.legalArrangements?.arrangements)
    ? ownership.legalArrangements.arrangements
    : [];

  if (!arrangements.length) return null;

  const nodeName = getNodeDisplayName(node, detectedId);
  const nodeType = clean(node?.type);
  const idxMatch = clean(detectedId).match(/^LA_rev_(\d+)$/);
  if (idxMatch) {
    const byIndex = arrangements[Number(idxMatch[1])];
    if (byIndex) {
      const sameName = !nodeName || clean(byIndex?.name) === nodeName;
      const sameType = !nodeType || legalArrangementType(byIndex) === nodeType;
      if (sameName && sameType) return byIndex;
    }
  }

  return (
    arrangements.find((arr) => {
      const sameName = clean(arr?.name) === nodeName;
      const sameType = !nodeType || legalArrangementType(arr) === nodeType;
      return sameName && sameType;
    }) || null
  );
}

function buildCopyDebugPayload({
  stage,
  accountHolderName,
  ownership,
  liveOwnershipGraph,
  uboApiPayload,
  updatePath,
  updatedValue,
}) {
  return {
    stage,
    accountHolderName,
    updatePath: Array.isArray(updatePath) ? updatePath : undefined,
    updatedValueSummary: Array.isArray(updatedValue)
      ? summarizeOwnershipRows(updatedValue)
      : undefined,
    storedAssistantTreeMermaid: ownership?.assistantTree?.mermaid || "",
    liveMermaid: liveOwnershipGraph?.mermaid || "",
    directShareholders: summarizeOwnershipRows(ownership?.shareholders || []),
    indirectUbos: summarizeOwnershipRows(ownership?.ubos || []),
    legalArrangements: summarizeOwnershipRows(
      ownership?.legalArrangements?.arrangements || []
    ),
    liveNodes: summarizeGraphNodes(liveOwnershipGraph?.nodes || []),
    liveFlowLines: Array.isArray(liveOwnershipGraph?.flowLines)
      ? liveOwnershipGraph.flowLines
      : [],
    apiPayload: {
      initialAHType: clean(uboApiPayload?.initialAHType),
      nodes: summarizeGraphNodes(uboApiPayload?.nodes || []),
      rawNodes: uboApiPayload?.nodes || [],
    },
  };
}

function logCopyDebugPayload(payload) {
  return;
}

function articleFor(label) {
  return /^[aeiou]/i.test(clean(label)) ? "an" : "a";
}

function completionMethodLabel(ownership) {
  const desktopMethod = clean(ownership?.desktopClientCompletionMethod);
  const mobileMode = clean(ownership?.mobileEntryMode);

  if (desktopMethod === "visual") return "visual ownership builder";
  if (desktopMethod === "guided") return "guided chat assistant";
  if (desktopMethod === "manual") return "manual ownership entry";
  if (mobileMode === "assistant") return "guided chat assistant";
  if (mobileMode === "manual") return "manual ownership entry";
  if (ownership?.assistantGenerated) return "guided chat assistant";
  if (ownership?.assistantTree?.mermaid) return "visual ownership builder";
  return "ownership details provided by the client";
}

const CLIENT_TYPE_OPTIONS = [
  {
    value: "operating",
    title: "Operating company",
    hint: "A standard non-quoted company carrying out commercial business activities.",
    iconSrc: iconProduction,
  },
  {
    value: "holding",
    title: "Holding company",
    hint: "A company primarily used to hold interests in other companies.",
    iconSrc: iconHolding,
  },
  {
    value: "pic",
    title: "Domiciliary company (PIC)",
    hint: "A personal investment company used to hold assets or investments.",
    iconSrc: iconPic,
  },
  {
    value: "trust",
    title: "Trust",
    hint: "A legal arrangement where trustees hold or manage assets for beneficiaries.",
    iconSrc: iconTrust,
  },
  {
    value: "foundation",
    title: "Foundation",
    hint: "A legal arrangement with separate legal personality.",
    iconSrc: iconFoundation,
  },
  {
    value: "quoted",
    title: "Quoted company",
    hint: "A company listed on a regulated stock exchange.",
    iconSrc: iconQuoted,
  },
];

function accountHolderTypeLabel(ownership) {
  const profile = clean(ownership?.desktopClientEntityType);
  const holdingKind = clean(ownership?.desktopClientHoldingKind);
  const mobileMode = clean(ownership?.mobileAssistantDraft?.preliminaryMode);
  const flags = ownership?.uboFlags || {};

  if (profile === "operating" || mobileMode === "noneOfAbove") {
    return "non quoted operating company";
  }
  if (profile === "holding") {
    if (holdingKind === "active") return "active holding";
    if (holdingKind === "passive") return "passive holding";
    return "holding company";
  }
  if (profile === "pic" || mobileMode === "pic") {
    return "personal investment company (PIC)";
  }
  if (profile === "quoted" || mobileMode === "quoted" || flags.quotedCompany) {
    return "quoted company";
  }
  if (profile === "trust" || mobileMode === "trust" || flags.accountHolderTrust) {
    return "trust";
  }
  if (
    profile === "foundation" ||
    mobileMode === "foundation" ||
    flags.accountHolderFoundation
  ) {
    return "foundation";
  }
  if (flags.pctNotMeaningful) return "passive holding";
  return "non quoted operating company";
}

function escapeRegExp(value) {
  return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function normalizePercent(value) {
  const raw = clean(value).replace(/%/g, "");
  if (!raw) return "";
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return String(Math.round(n * 100) / 100);
}

function directShareholderNodeIdForNode(node) {
  const nodeId = clean(node?.ID);
  if (!nodeId || nodeId === "X0") return "";
  const parents = Array.isArray(node?.parentIDs)
    ? node.parentIDs.map((id) => clean(id)).filter(Boolean)
    : [];
  const x0Index = parents.indexOf("X0");
  if (x0Index < 0) return "";
  if (x0Index === 0) return nodeId;
  return parents[x0Index - 1] || "";
}

function nodeLabelForMermaid(node, initialAHType) {
  return toMermaidHtmlLabel(labelForNodeHtml(node, initialAHType));
}

function replaceNodeLabelInMermaid(source, nodeId, nextLabel) {
  const id = escapeRegExp(nodeId);
  const re = new RegExp(`(${id}\\s*\\(")([^"]*)("\\))`, "g");
  return String(source || "").replace(re, `$1${nextLabel}$3`);
}

function setEdgePercentInMermaid(source, parentId, childId, percent) {
  const parent = escapeRegExp(parentId);
  const child = escapeRegExp(childId);
  const pct = normalizePercent(percent);
  const nextLabel = pct ? `${pct}%` : "";
  let out = String(source || "");

  const labelledEdge = new RegExp(
    `(${parent}\\s*--\\>)\\s*\\|[^|]*\\|\\s*(${child}(?=\\b|\\s|\\(|\\[|\\{))`,
    "g"
  );
  let changed = false;
  out = out.replace(
    labelledEdge,
    (...args) => {
      changed = true;
      return nextLabel ? `${args[1]}|"${nextLabel}"| ${args[2]}` : `${args[1]} ${args[2]}`;
    }
  );

  if (nextLabel && !changed) {
    const unlabelledEdge = new RegExp(
      `(${parent}\\s*-->)\\s*(${child}(?=\\b|\\s|\\(|\\[|\\{))`,
      "g"
    );
    out = out.replace(unlabelledEdge, `$1|"${nextLabel}"| $2`);
  }

  return out;
}

function setIncomingEdgePercentInMermaid(source, childId, percent, preferredParentId = "") {
  let out = String(source || "");
  if (preferredParentId) {
    const nextOut = setEdgePercentInMermaid(out, preferredParentId, childId, percent);
    if (nextOut !== out) return nextOut;
  }

  const child = escapeRegExp(childId);
  const pct = normalizePercent(percent);
  const nextLabel = pct ? `${pct}%` : "";
  const parent = "([A-Za-z_][\\w-]*)";

  const labelledIncoming = new RegExp(
    `(${parent}\\s*--\\>)\\s*\\|[^|]*\\|\\s*(${child}(?=\\b|\\s|\\(|\\[|\\{))`,
    "g"
  );
  let changed = false;
  out = out.replace(labelledIncoming, (...args) => {
    changed = true;
    return nextLabel ? `${args[1]}|"${nextLabel}"| ${args[3]}` : `${args[1]} ${args[3]}`;
  });

  if (nextLabel && !changed) {
    const unlabelledIncoming = new RegExp(
      `(${parent}\\s*-->)\\s*(${child}(?=\\b|\\s|\\(|\\[|\\{))`,
      "g"
    );
    out = out.replace(unlabelledIncoming, `$1|"${nextLabel}"| $3`);
  }

  return out;
}

function nodeTypeForShareholder(row) {
  return shareholderRowNodeType(row) || "Non Quoted Operational Company";
}

// Copy a row's personal details onto its tree node, so the UBO API and the CDB forms use
// what the table shows. The table is authoritative: a cleared value clears the node's too.
function applyRowDetailsToNode(node, row) {
  if (!node || !row) return node;
  const individual = clean(node.type) === "Individual";
  if (individual) node.nationality = clean(row.nationality);
  else delete node.nationality;
  const residence = clean(row.countryOfResidence);
  node.countryOfResidence = residence;
  if ("domicile" in node) node.domicile = residence;
  const date = rowDateText(row.dobOrIncorporationDate);
  if (individual) {
    node.dateOfBirth = date;
    delete node.incorporationDate;
  } else {
    node.incorporationDate = date;
    delete node.dateOfBirth;
  }
  return node;
}

// A legal arrangement row seen as an ownership row, for the shared ID rule (ownershipRowNodeIds).
function arrangementIdRow(arr) {
  return { treeNodeId: clean(arr?._treeArrangementNodeId || arr?.treeNodeId || arr?.subjectTreeNodeId) };
}

function isPicShareholder(row) {
  return (
    clean(row?.shareholderEntityType) === "PIC" ||
    clean(row?.entityCategory).toLowerCase() === "pic" ||
    !!row?.isPIC
  );
}

function isPicNode(node) {
  return (
    clean(node?.type) === "PIC" ||
    clean(node?.entityCategory).toLowerCase() === "pic" ||
    !!node?.isPIC
  );
}

// Shareholders of an operating company or active holding carry their % in it; below a PIC,
// passive holding, trust or foundation the % is not meaningful and is not shown.
const PERCENT_PARENT_TYPES = new Set([
  "Non Quoted Operational Company", "Operating Company", "HoldingActive", "Holding (Active)",
]);

function nodeHoldsPercent(parent) {
  return PERCENT_PARENT_TYPES.has(clean(parent?.type)) && !isPicNode(parent);
}

// A missing % stays missing (the API flags it) instead of defaulting to 0 or 100.
function percentOrUndefined(value) {
  const pct = normalizePercent(value);
  return pct ? Number(pct) : undefined;
}

function edgeForPercent(percent, { showPercent = true } = {}) {
  const pct = showPercent ? normalizePercent(percent) : "";
  return pct ? `-->|"${pct}%"|` : `-->`;
}

function desktopTypeToNodeType(desktopType, holdingKind) {
  if (desktopType === "pic") return "PIC";
  if (desktopType === "holding") {
    return holdingKind === "passive" ? "HoldingPassive" : "HoldingActive";
  }
  if (desktopType === "trust") return "Trust";
  if (desktopType === "foundation") return "Foundation";
  if (desktopType === "quoted") return "Quoted Company";
  return "Non Quoted Operational Company";
}

function mobileAssistantModeToNodeType(mode) {
  if (mode === "pic") return "PIC";
  if (mode === "holdingActive") return "HoldingActive";
  if (mode === "holdingPassive") return "HoldingPassive";
  if (mode === "trust") return "Trust";
  if (mode === "foundation") return "Foundation";
  if (mode === "quoted") return "Quoted Company";
  return "Non Quoted Operational Company";
}

function fallbackFlagsToAccountHolderNodeType(flags = {}) {
  if (flags.accountHolderTrust) return "Trust";
  if (flags.accountHolderFoundation) return "Foundation";
  if (flags.quotedCompany) return "Quoted Company";
  if (flags.pctNotMeaningful) return "PIC";
  return "";
}

function normalizeAccountHolderNodeType(value) {
  const raw = clean(value);
  const key = raw.toLowerCase();
  if (!raw) return "";
  if (raw === "PIC" || key.includes("domiciliary") || key.includes("personal investment")) {
    return "PIC";
  }
  if (raw === "HoldingActive" || key === "active holding" || key.includes("holding (active)")) {
    return "HoldingActive";
  }
  if (raw === "HoldingPassive" || key === "passive holding" || key.includes("holding (passive)")) {
    return "HoldingPassive";
  }
  if (raw === "Trust" || key === "trust") return "Trust";
  if (raw === "Foundation" || key === "foundation") return "Foundation";
  if (raw === "Quoted Company" || key === "quoted company") return "Quoted Company";
  if (
    raw === "Non Quoted Operational Company" ||
    key === "operating company" ||
    key === "non quoted operating company"
  ) {
    return "Non Quoted Operational Company";
  }
  return raw;
}

function mermaidIdCandidates(rawTreeNodeId, mermaidSource) {
  const raw = clean(rawTreeNodeId);
  if (!raw) return [];
  const sanitized = raw.replace(/[^a-zA-Z0-9_]/g, "_");
  const candidates = [`G_${sanitized}`, `A_${sanitized}`, `A_${raw}`, raw];
  const seen = new Set();
  return candidates.filter((candidate) => {
    if (!candidate || seen.has(candidate)) return false;
    seen.add(candidate);
    const id = escapeRegExp(candidate);
    const re = new RegExp(
      `(?:^|[\\s;])${id}(?=\\s*(?:\\(|\\[|\\{|-->|---|$))`
    );
    return re.test(String(mermaidSource || ""));
  });
}

function nodeIdsFromFlowLines(flowLines = []) {
  const ids = new Set(["X0"]);

  (Array.isArray(flowLines) ? flowLines : []).forEach((line) => {
    const text = String(line || "");
    // Mobile assistant IDs are random base-36 strings and can start with a digit.
    const sourceMatch = text.match(/^\s*([A-Za-z0-9_]+)\s*[-=]+>/);
    if (sourceMatch?.[1]) ids.add(sourceMatch[1]);

    const nodeMatches = Array.from(
      text.matchAll(/\b([A-Za-z0-9_]+)\s*\(/g)
    );
    nodeMatches.forEach((match) => {
      if (match?.[1]) ids.add(match[1]);
    });
  });

  return ids;
}

function removeSnapshotOnlyFields(node) {
  if (!node || typeof node !== "object") return node;
  const { _reviewEdgePercent, ...rest } = node;
  return rest;
}

function buildSnapshotFromOriginalMermaid({
  snapshot,
  ownership,
  accountHolderName,
  isPIC = false,
  overrideX0Type = null,
}) {
  let mermaid = String(snapshot?.mermaid || "");
  if (!mermaid) return snapshot;

  const initialAHType = clean(snapshot?.initialAHType);
  const x0Type = overrideX0Type || initialAHType;
  mermaid = replaceNodeLabelInMermaid(
    mermaid,
    "X0",
    nodeLabelForMermaid(
      { ID: "X0", name: accountHolderName, type: x0Type },
      x0Type
    )
  );

  const shareholders = Array.isArray(ownership?.shareholders)
    ? ownership.shareholders
    : Array.isArray(ownership?.directShareholders)
    ? ownership.directShareholders
    : [];
  const ubos = Array.isArray(ownership?.ubos)
    ? ownership.ubos
    : Array.isArray(ownership?.controllingPersonsNatural)
    ? ownership.controllingPersonsNatural
    : [];

  const shareholderRowIdToMermaidId = new Map();
  const picMermaidNodeIds = new Set();
  shareholders.forEach((row) => {
    const ids = mermaidIdCandidates(row?.treeNodeId, mermaid);
    const nodeId = ids[0] || "";
    if (!nodeId) return;
    if (row?._rowId) shareholderRowIdToMermaidId.set(clean(row._rowId), nodeId);
    if (isPicShareholder(row)) picMermaidNodeIds.add(nodeId);
    mermaid = replaceNodeLabelInMermaid(
      mermaid,
      nodeId,
      nodeLabelForMermaid(
        {
          ID: nodeId,
          name: clean(row?.name),
          type: nodeTypeForShareholder(row),
        },
        x0Type
      )
    );
    mermaid = setEdgePercentInMermaid(
      mermaid,
      "X0",
      nodeId,
      isPIC ? "" : row?.ownershipPercent
    );
  });

  ubos.forEach((row) => {
    const ids = mermaidIdCandidates(row?.treeNodeId, mermaid);
    const nodeId = ids[0] || "";
    if (!nodeId) return;
    mermaid = replaceNodeLabelInMermaid(
      mermaid,
      nodeId,
      nodeLabelForMermaid(
        {
          ID: nodeId,
          name: clean(row?.name),
          type:
            clean(row?.shareholderEntityType) === "Quoted Company"
              ? "Quoted Company"
              : "Individual",
        },
        initialAHType
      )
    );

    const parentId =
      shareholderRowIdToMermaidId.get(clean(row?.relatedShareholderRowId)) ||
      mermaidIdCandidates(row?.relatedShareholderNodeId, mermaid)[0] ||
      "";
    mermaid = setIncomingEdgePercentInMermaid(
      mermaid,
      nodeId,
      picMermaidNodeIds.has(parentId) ? "" : row?.effectiveOwnershipPercent,
      parentId
    );
  });

  return {
    ...snapshot,
    mermaid,
    initialAHType: x0Type,
    nodes: Array.isArray(snapshot?.nodes)
      ? snapshot.nodes.map((node) =>
          clean(node?.ID || node?.id) === "X0"
            ? { ...node, ID: clean(node?.ID || node?.id) || "X0", name: accountHolderName, type: x0Type }
            : node
        )
      : snapshot?.nodes,
  };
}

export function normalizeNodeForApi(node) {
  return {
    ...node,
    ID: clean(node?.ID || node?.id),
    name: clean(node?.name),
    type: clean(node?.type) || "Entity",
    parentIDs: Array.isArray(node?.parentIDs) ? node.parentIDs : [],
    percent:
      node?.percent === "" || node?.percent == null
        ? undefined
        : Number(node.percent),
  };
}

function normalizeNodesForApi(nodes) {
  const normalized = (Array.isArray(nodes) ? nodes : [])
    .map(normalizeNodeForApi)
    .filter((node) => node.ID);
  const nodeById = new Map(normalized.map((node) => [clean(node.ID), node]));

  const parentChainFor = (node, seen = new Set()) => {
    const rawParents = Array.isArray(node?.parentIDs)
      ? node.parentIDs.map((id) => clean(id)).filter(Boolean)
      : [];
    const primaryParentId = rawParents[0] || "";
    if (!primaryParentId) return [];
    if (primaryParentId === "X0") return ["X0"];
    if (seen.has(primaryParentId)) return rawParents;

    const nextSeen = new Set(seen);
    nextSeen.add(primaryParentId);
    const parentNode = nodeById.get(primaryParentId);
    const parentChain = parentNode
      ? parentChainFor(parentNode, nextSeen)
      : rawParents.slice(1);
    return [primaryParentId, ...parentChain].filter(Boolean);
  };

  const withParents = normalized.map((node) => ({
    ...node,
    parentIDs: parentChainFor(node),
  }));

  const maxTypeNoByParent = new Map();
  withParents.forEach((node) => {
    const parentKey = clean(node?.parentIDs?.[0]) || "__root__";
    const typeNo = Number(node?.typeNo);
    if (!Number.isFinite(typeNo)) return;
    maxTypeNoByParent.set(
      parentKey,
      Math.max(maxTypeNoByParent.get(parentKey) || 0, typeNo)
    );
  });

  const nextTypeNoByParent = new Map(maxTypeNoByParent);
  return withParents.map((node) => {
    const parentKey = clean(node?.parentIDs?.[0]) || "__root__";
    const existingTypeNo = Number(node?.typeNo);
    const typeNo = Number.isFinite(existingTypeNo)
      ? existingTypeNo
      : (nextTypeNoByParent.set(
          parentKey,
          (nextTypeNoByParent.get(parentKey) || 0) + 1
        ),
        nextTypeNoByParent.get(parentKey));
    const existingLevel = Number(node?.level);
    const level = Number.isFinite(existingLevel)
      ? existingLevel
      : node.parentIDs.length;
    const type = clean(node?.type);

    return {
      ...node,
      roles: node?.roles === undefined ? null : node.roles,
      level,
      typeNo,
      boolADE:
        typeof node?.boolADE === "boolean"
          ? node.boolADE
          : type !== "Individual",
    };
  });
}

function getNodeDisplayName(node, fallback = "") {
  return clean(node?.name) || clean(node?.label) || clean(fallback) || "Unnamed node";
}

function findNodeByApiId(nodes, id) {
  const raw = clean(id);
  if (!raw) return null;
  const sanitized = raw.replace(/[^a-zA-Z0-9_]/g, "_");
  const candidates = [raw, `G_${sanitized}`, `A_${sanitized}`, `A_${raw}`];
  return (
    nodes.find((node) => candidates.includes(clean(node?.ID || node?.id))) ||
    null
  );
}

export function buildLiveReviewOwnershipSnapshot({ ownership, accountHolderName }) {
  // A mobile-assistant diagram has no boxes of its own: they are built from its full structure.
  const savedTree = ownershipTreeWithMobileNodes(ownership);
  const snapshot = savedTree && typeof savedTree === "object" ? savedTree : null;

  const allShareholders = Array.isArray(ownership?.shareholders)
    ? ownership.shareholders
    : Array.isArray(ownership?.directShareholders)
    ? ownership.directShareholders
    : [];

  const allUbos = Array.isArray(ownership?.ubos)
    ? ownership.ubos
    : Array.isArray(ownership?.controllingPersonsNatural)
    ? ownership.controllingPersonsNatural
    : [];

  const allLegalArrangements = Array.isArray(ownership?.legalArrangements?.arrangements)
    ? ownership.legalArrangements.arrangements
    : [];

  const isPIC = !!ownership?.uboFlags?.pctNotMeaningful;
  const desktopEntityType = clean(ownership?.desktopClientEntityType);
  const desktopHoldingKind = clean(ownership?.desktopClientHoldingKind);
  const mobileAssistantMode = clean(ownership?.mobileAssistantDraft?.preliminaryMode);
  const overrideX0Type = desktopEntityType
    ? desktopTypeToNodeType(desktopEntityType, desktopHoldingKind)
    : mobileAssistantMode
    ? mobileAssistantModeToNodeType(mobileAssistantMode)
    : fallbackFlagsToAccountHolderNodeType(ownership?.uboFlags || {})
    ? fallbackFlagsToAccountHolderNodeType(ownership?.uboFlags || {})
    : normalizeAccountHolderNodeType(snapshot?.initialAHType)
    ? normalizeAccountHolderNodeType(snapshot?.initialAHType)
    : null;
  const shareholderByRowId = new Map(
    allShareholders
      .map((row) => [clean(row?._rowId), row])
      .filter(([rowId]) => !!rowId)
  );

  function arrangementIsAlreadyRepresentedByShareholder(arr) {
    if (!arr || String(arr?.source || "").trim().toLowerCase() !== "shareholder") {
      return false;
    }
    const shareholder = shareholderByRowId.get(clean(arr?.sourceRefRowId));
    if (!shareholder) return false;
    const holderType = String(
      shareholder?.holderType || shareholder?.shareholderEntityType || ""
    )
      .trim()
      .toLowerCase();
    return holderType.includes("trust") || holderType.includes("foundation");
  }

  // One node ID per row, by the shared rule (see ownershipRowNodeIds): rows keep their own ID,
  // others get SH_rev_n / UBO_rev_n without ever reusing an ID already taken.
  const rowShareholderIds = ownershipRowNodeIds(allShareholders, null, "SH_rev");
  const rowUboIds = ownershipRowNodeIds(allUbos, null, "UBO_rev");
  const rowArrangementIds = ownershipRowNodeIds(allLegalArrangements.map(arrangementIdRow), null, "LA_rev");

  function buildSnapshotFromRows({ baseSnapshot = null, x0Type, allowEmpty = false }) {
    if (!allowEmpty && !allShareholders.length && !allLegalArrangements.length) return baseSnapshot;
    const ahNode = { ID: "X0", name: accountHolderName, type: x0Type };
    const styleTail = `\nclassDef default fill:#BAD9CB,stroke:#BAD9CB,stroke-width:0px,color:#484848\n  linkStyle default stroke-width:2px,fill:none,stroke:#B0B0B0`;
    const base = `graph TD;X0("${nodeLabelForMermaid(ahNode, "")}")`;
    const shRowIdToId = new Map();
    // A % only where the parent carries one (see parentCarriesPercent): the client's shareholders
    // when the client is an operating company or active holding, and owners of such companies.
    const clientShowsPercent = !isPIC && nodeHoldsPercent({ type: x0Type });
    const shareholderTypeById = new Map(allShareholders.map((row, i) => [rowShareholderIds[i], nodeTypeForShareholder(row)]));
    const parentShowsPercent = (parentId) => nodeHoldsPercent({ type: shareholderTypeById.get(parentId) });
    const lines = allShareholders.map((row, i) => {
      const id = rowShareholderIds[i];
      if (row?._rowId) shRowIdToId.set(clean(row._rowId), id);
      const node = { ID: id, name: clean(row?.name) || "…", type: nodeTypeForShareholder(row), parentIDs: ["X0"] };
      const edge = edgeForPercent(row?.ownershipPercent, { showPercent: clientShowsPercent });
      return `X0 ${edge} ${id}("${nodeLabelForMermaid(node, x0Type)}")`;
    });
    const uboLines = [];
    allUbos.forEach((row, i) => {
      const parentId =
        clean(row?.relatedShareholderNodeId) ||
        shRowIdToId.get(clean(row?.relatedShareholderRowId)) ||
        "";
      if (!parentId) return;
      const id = rowUboIds[i];
      const uboNode = { ID: id, name: clean(row?.name) || "…", type: "Individual", parentIDs: [parentId] };
      const edge = edgeForPercent(row?.effectiveOwnershipPercent || row?.ownershipPercent, {
        showPercent: parentShowsPercent(parentId),
      });
      uboLines.push(`${parentId} ${edge} ${id}("${nodeLabelForMermaid(uboNode, "")}")`);
    });
    const laLines = [];
    // A trust/foundation held by an operating company or active holding carries its % in it.
    const laPercent = (arr, parentId) =>
      parentId !== "X0" && parentShowsPercent(parentId) ? arr?.ownershipPercent : "";
    allLegalArrangements.forEach((arr, i) => {
      if (arrangementIsAlreadyRepresentedByShareholder(arr)) return;
      const parentId =
        clean(arr?.sourceRefNodeId) ||
        shRowIdToId.get(clean(arr?.sourceRefRowId)) ||
        "";
      if (!parentId) return;
      const id = rowArrangementIds[i];
      const t = String(arr?.arrangementType || "").trim() === "Foundation" ? "Foundation" : "Trust";
      const laNode = { ID: id, name: clean(arr?.name) || "…", type: t, parentIDs: [parentId] };
      laLines.push(`${parentId} ${edgeForPercent(laPercent(arr, parentId))} ${id}("${nodeLabelForMermaid(laNode, "")}")`);
    });
    const allLines = [...lines, ...uboLines, ...laLines];
    const nodes = [ahNode];
    allShareholders.forEach((row, i) => {
      nodes.push(applyRowDetailsToNode({
        ID: rowShareholderIds[i],
        name: clean(row?.name) || "…",
        type: nodeTypeForShareholder(row),
        parentIDs: ["X0"],
        percent: clientShowsPercent ? percentOrUndefined(row?.ownershipPercent) : undefined,
      }, row));
    });
    allUbos.forEach((row, i) => {
      const parentId =
        clean(row?.relatedShareholderNodeId) ||
        shRowIdToId.get(clean(row?.relatedShareholderRowId)) ||
        "";
      if (!parentId) return;
      nodes.push(applyRowDetailsToNode({
        ID: rowUboIds[i],
        name: clean(row?.name) || "…",
        type: "Individual",
        parentIDs:
          parentId === "X0"
            ? ["X0"]
            : [parentId, ...(parentId ? ["X0"] : [])],
        percent: parentShowsPercent(parentId)
          ? percentOrUndefined(row?.ownershipPercent || row?.effectiveOwnershipPercent)
          : undefined,
      }, row));
    });
    allLegalArrangements.forEach((arr, i) => {
      if (arrangementIsAlreadyRepresentedByShareholder(arr)) return;
      const parentId =
        clean(arr?.sourceRefNodeId) ||
        shRowIdToId.get(clean(arr?.sourceRefRowId)) ||
        "";
      if (!parentId) return;
      nodes.push({
        ID: rowArrangementIds[i],
        name: clean(arr?.name) || "…",
        type:
          String(arr?.arrangementType || "").trim() === "Foundation"
            ? "Foundation"
            : "Trust",
        parentIDs:
          parentId === "X0"
            ? ["X0"]
            : [parentId, ...(parentId ? ["X0"] : [])],
        percent: percentOrUndefined(laPercent(arr, parentId)),
      });
    });
    return {
      ...(baseSnapshot || {}),
      mermaid: `${base};${allLines.join(";")}${styleTail}`,
      nodes,
      flowLines: allLines,
      initialAHType: x0Type,
      source: baseSnapshot?.source || "manualEntry",
    };
  }

  // Manual snapshots are derived from the current rows. Reusing their generated
  // nodes and appending those same rows duplicates IDs after save/reopen.
  if (snapshot?.source === "manualEntry") {
    return buildSnapshotFromRows({
      baseSnapshot: snapshot,
      x0Type: overrideX0Type || "Non Quoted Operational Company",
      allowEmpty: true,
    });
  }

  // No assistantTree — build a minimal diagram from shareholders + UBOs + legal arrangements.
  if (!snapshot?.mermaid) {
    return buildSnapshotFromRows({
      baseSnapshot: snapshot,
      x0Type: overrideX0Type || "Non Quoted Operational Company",
    });
  }

  const initialAHType = clean(snapshot?.initialAHType);
  const sourceNodes = Array.isArray(snapshot?.nodes) ? snapshot.nodes : [];

  // Build the base snapshot (either from nodes or from original mermaid source)
  let baseResult;
  if (!sourceNodes.length) {
    const x0Type = overrideX0Type || clean(snapshot?.initialAHType) || "Non Quoted Operational Company";
    baseResult =
      buildSnapshotFromRows({ baseSnapshot: snapshot, x0Type }) ||
      buildSnapshotFromOriginalMermaid({ snapshot, ownership, accountHolderName, isPIC, overrideX0Type });
  } else {
    // full nodes-based patching (existing logic below)
    baseResult = null;
  }

  // Helper: append new shareholders to any already-built snapshot
  function appendNewShareholders(result) {
    if (!result?.mermaid) return result;
    const newRows = allShareholders.filter((r) => !clean(r?.treeNodeId));
    if (!newRows.length) return result;
    const styleSplit = result.mermaid.indexOf("\nclassDef");
    const mermaidBase = styleSplit >= 0 ? result.mermaid.slice(0, styleSplit) : result.mermaid;
    const styleTail = styleSplit >= 0 ? result.mermaid.slice(styleSplit) : "";
    const extraLines = newRows.map((row) => {
      const id = rowShareholderIds[allShareholders.indexOf(row)];
      const newNode = { ID: id, name: clean(row?.name) || "…", type: nodeTypeForShareholder(row), parentIDs: ["X0"] };
      const edge = edgeForPercent(row?.ownershipPercent, { showPercent: !isPIC });
      return `X0 ${edge} ${id}("${nodeLabelForMermaid(newNode, initialAHType)}")`;
    });
    return { ...result, mermaid: `${mermaidBase};${extraLines.join(";")}${styleTail}` };
  }

  // Helper: append UBOs without treeNodeId as new individual nodes
  function appendNewUbos(result) {
    if (!result?.mermaid) return result;
    const ubosToAdd = allUbos.filter((r) => !clean(r?.treeNodeId));
    if (!ubosToAdd.length) return result;
    const styleSplit = result.mermaid.indexOf("\nclassDef");
    const mermaidBase = styleSplit >= 0 ? result.mermaid.slice(0, styleSplit) : result.mermaid;
    const styleTail = styleSplit >= 0 ? result.mermaid.slice(styleSplit) : "";
    // Build shareholder _rowId → mermaid node id map
    const shRowIdToMermaidId = new Map();
    allShareholders.forEach((sh, shIndex) => {
      const rowId = clean(sh?._rowId);
      if (!rowId) return;
      const treeId = clean(sh?.treeNodeId);
      if (treeId) {
        const candidates = mermaidIdCandidates(treeId, result.mermaid);
        if (candidates[0]) shRowIdToMermaidId.set(rowId, candidates[0]);
      } else {
        shRowIdToMermaidId.set(rowId, rowShareholderIds[shIndex]);
      }
    });
    // A % only below an operating company or active holding (not a PIC, passive holding, …).
    const parentIdIsPic = (parentId) => {
      const id = clean(parentId);
      if (!id) return false;
      return allShareholders.some((sh) => {
        if (nodeHoldsPercent({ type: nodeTypeForShareholder(sh) })) return false;
        const rowId = clean(sh?._rowId);
        if (rowId && shRowIdToMermaidId.get(rowId) === id) return true;
        return mermaidIdCandidates(sh?.treeNodeId, result.mermaid)[0] === id;
      });
    };
    const extraLines = [];
    ubosToAdd.forEach((row, i) => {
      const parentId =
        mermaidIdCandidates(row?.relatedShareholderNodeId, result.mermaid)[0] ||
        shRowIdToMermaidId.get(clean(row?.relatedShareholderRowId)) ||
        "";
      if (!parentId) return;
      const id = rowUboIds[allUbos.indexOf(row)];
      const uboNode = { ID: id, name: clean(row?.name) || "…", type: "Individual", parentIDs: [parentId] };
      const edge = edgeForPercent(row?.effectiveOwnershipPercent, {
        showPercent: !parentIdIsPic(parentId),
      });
      extraLines.push(`${parentId} ${edge} ${id}("${nodeLabelForMermaid(uboNode, initialAHType)}")`);
    });
    if (!extraLines.length) return result;
    return { ...result, mermaid: `${mermaidBase};${extraLines.join(";")}${styleTail}` };
  }

  // Helper: append legal arrangements (Trust/Foundation) without treeNodeId as new nodes
  function appendNewLegalArrangements(result) {
    if (!result?.mermaid) return result;
    if (!allLegalArrangements.length) return result;
    const styleSplit = result.mermaid.indexOf("\nclassDef");
    const mermaidBase = styleSplit >= 0 ? result.mermaid.slice(0, styleSplit) : result.mermaid;
    const styleTail = styleSplit >= 0 ? result.mermaid.slice(styleSplit) : "";
    // Build shareholder _rowId → mermaid node id map
    const shRowIdToMermaidId = new Map();
    allShareholders.forEach((sh, shIndex) => {
      const rowId = clean(sh?._rowId);
      if (!rowId) return;
      const treeId = clean(sh?.treeNodeId);
      if (treeId) {
        const candidates = mermaidIdCandidates(treeId, result.mermaid);
        if (candidates[0]) shRowIdToMermaidId.set(rowId, candidates[0]);
      } else {
        shRowIdToMermaidId.set(rowId, rowShareholderIds[shIndex]);
      }
    });
    const extraLines = [];
    allLegalArrangements.forEach((arr, i) => {
      if (arrangementIsAlreadyRepresentedByShareholder(arr)) return;
      const parentId =
        mermaidIdCandidates(arr?.sourceRefNodeId, result.mermaid)[0] ||
        shRowIdToMermaidId.get(clean(arr?.sourceRefRowId)) ||
        "";
      if (!parentId) return;
      const id = rowArrangementIds[i];
      const t = String(arr?.arrangementType || "").trim() === "Foundation" ? "Foundation" : "Trust";
      const laNode = { ID: id, name: clean(arr?.name) || "…", type: t, parentIDs: [parentId] };
      extraLines.push(`${parentId} --> ${id}("${nodeLabelForMermaid(laNode, initialAHType)}")`);
    });
    if (!extraLines.length) return result;
    return { ...result, mermaid: `${mermaidBase};${extraLines.join(";")}${styleTail}` };
  }

  if (baseResult !== null) {
    return appendNewLegalArrangements(appendNewUbos(appendNewShareholders(baseResult)));
  }

  // A saved live snapshot can already contain nodes added from manual rows.
  // Reuse their generated IDs when those rows have not acquired treeNodeId yet.
  // Also repair snapshots previously saved with duplicate node IDs.
  const nodes = [...new Map(sourceNodes.map((node) => [clean(node?.ID), { ...node }])).values()];
  const nodeById = new Map(nodes.map((node) => [clean(node?.ID), node]));
  const parentNodeShowsPercent = (parentId) => nodeHoldsPercent(nodeById.get(clean(parentId)));

  const x0ShowsPercent =
    !isPIC && nodeHoldsPercent({ type: overrideX0Type || clean(nodeById.get("X0")?.type) || initialAHType });

  const shareholderIds = ownershipRowNodeIds(allShareholders, snapshot, 'SH_rev');
  const shareholders = allShareholders.map((row, index) => ({ ...row, treeNodeId: shareholderIds[index] }));
  const uboIds = ownershipRowNodeIds(allUbos, snapshot, 'UBO_rev');
  const ubos = allUbos.map((row, index) => ({ ...row, treeNodeId: uboIds[index] }));

  const shareholderRowIdToTreeNodeId = new Map();
  const shareholderByTreeNodeId = new Map();
  shareholders.forEach((row) => {
    const treeNodeId = clean(row?.treeNodeId);
    if (!treeNodeId) return;
    shareholderByTreeNodeId.set(treeNodeId, row);
    if (row?._rowId) shareholderRowIdToTreeNodeId.set(clean(row._rowId), treeNodeId);
  });

  shareholders.forEach((row) => {
    const treeNodeId = clean(row?.treeNodeId);
    const node = treeNodeId ? nodeById.get(treeNodeId) : null;
    if (!node) return;
    node.name = clean(row?.name) || "…";
    // The table is authoritative for the type too (e.g. operating company → PIC, or back).
    const rowType = shareholderRowNodeType(row);
    if (rowType) {
      node.type = rowType;
      if (rowType !== "PIC") {
        delete node.isPIC;
        if (clean(node.entityCategory).toLowerCase() === "pic") delete node.entityCategory;
      }
    }
    applyRowDetailsToNode(node, row);
    const pct = normalizePercent(row?.ownershipPercent);
    const directOfClient = clean(node?.parentIDs?.[0]) === "X0";
    // No % at all where the client carries none (PIC, passive holding, trust, foundation).
    if (directOfClient && !x0ShowsPercent) delete node.percent;
    else if (pct) node.percent = pct;
    // A % cleared in the table is cleared on the node too, where the client shows %.
    else if (directOfClient) delete node.percent;
  });

  // New shareholders with no matching tree node — append as new nodes
  const newFlowLines = [];
  const newShareholderRowIdToMermaidId = new Map();
  shareholders.forEach((row) => {
    const treeNodeId = clean(row?.treeNodeId);
    if (treeNodeId && nodeById.get(treeNodeId)) return; // already in tree
    const id = treeNodeId;
    if (row?._rowId) newShareholderRowIdToMermaidId.set(clean(row._rowId), id);
    const newNode = {
      ID: id,
      name: clean(row?.name) || "…",
      type: nodeTypeForShareholder(row),
      parentIDs: ["X0"],
      percent: percentOrUndefined(row?.ownershipPercent),
    };
    if (isPicShareholder(row)) newNode.type = "PIC";
    applyRowDetailsToNode(newNode, row);
    nodes.push(newNode);
    nodeById.set(id, newNode);
    const edge = edgeForPercent(row?.ownershipPercent, { showPercent: !isPIC });
    newFlowLines.push(`X0 ${edge} ${id}("${nodeLabelForMermaid(newNode, initialAHType)}")`);
  });

  const claimedNodeIds = new Set();
  ubos.forEach((row) => {
    const explicitNodeId = clean(
      row?.treeNodeId || row?.subjectTreeNodeId || row?.subjectNodeId
    );
    let node = explicitNodeId ? nodeById.get(explicitNodeId) : null;

    // Mark explicitly-matched nodes as claimed so position-matching skips them
    if (node) claimedNodeIds.add(clean(node.ID));

    let directShareholderNodeId = clean(row?.relatedShareholderNodeId);
    if (!directShareholderNodeId && row?.relatedShareholderRowId) {
      directShareholderNodeId =
        shareholderRowIdToTreeNodeId.get(clean(row.relatedShareholderRowId)) ||
        newShareholderRowIdToMermaidId.get(clean(row.relatedShareholderRowId)) ||
        "";
    }
    if (!directShareholderNodeId) return;

    if (!node) {
      const id = explicitNodeId;
      const rowPercent = normalizePercent(
        row?.ownershipPercent || row?.effectiveOwnershipPercent
      );
      const showsPercent = parentNodeShowsPercent(directShareholderNodeId);
      const newNode = {
        ID: id,
        name: clean(row?.name),
        type: "Individual",
        parentIDs:
          directShareholderNodeId === "X0"
            ? ["X0"]
            : [directShareholderNodeId, "X0"],
        // % held in the parent company; never assumed when missing. Below a PIC or passive
        // holding the % is not applicable: none is stored (the engines count that link as 100%).
        percent: showsPercent ? percentOrUndefined(rowPercent) : undefined,
      };
      applyRowDetailsToNode(newNode, row);
      nodes.push(newNode);
      nodeById.set(id, newNode);
      const edge = edgeForPercent(rowPercent, { showPercent: showsPercent });
      newFlowLines.push(`${directShareholderNodeId} ${edge} ${id}("${nodeLabelForMermaid(newNode, initialAHType)}")`);
      return;
    }

    // Direct shareholder details are authoritative when the same person also
    // appears in the UBO list. An empty edited name must clear the saved label.
    if (!shareholderByTreeNodeId.has(clean(node.ID))) {
      node.name = clean(row?.name) || "…";
      applyRowDetailsToNode(node, row);
    }
    const effectivePercent = normalizePercent(row?.effectiveOwnershipPercent);
    if (clean(node?.parentIDs?.[0]) === "X0") {
      // For a direct owner, effective ownership is the edge percentage itself.
      // Keep the node used by validation/API in sync with the displayed edge.
      // A shareholder row takes precedence when the person appears in both lists.
      if (!shareholderByTreeNodeId.has(clean(node.ID))) {
        if (x0ShowsPercent) node.percent = effectivePercent;
        else delete node.percent;
      }
      node._reviewEdgePercent = node.percent;
    } else {
      // Label the link with the % held in the parent company (as entered in the builder),
      // not the effective % in the client.
      node._reviewEdgePercent = normalizePercent(node.percent) || effectivePercent;
    }
  });

  // Legal arrangements from the visual builder already exist as tree nodes.
  // Reuse those nodes so the review snapshot does not draw a duplicate LA_rev node.
  // Manually added ones get LA_rev_n by the shared rule, so a saved snapshot's LA_rev node is
  // reused rather than drawn a second time.
  const arrangementIds = ownershipRowNodeIds(allLegalArrangements.map(arrangementIdRow), snapshot, "LA_rev");
  const claimedArrangementIds = new Set();
  allLegalArrangements.forEach((arr, index) => {
    if (arrangementIsAlreadyRepresentedByShareholder(arr)) return;
    const t = String(arr?.arrangementType || "").trim() === "Foundation" ? "Foundation" : "Trust";
    const id = arrangementIds[index];
    const existingArrangementNode = nodeById.get(id);
    if (existingArrangementNode) {
      existingArrangementNode.name = clean(arr?.name) || "…";
      existingArrangementNode.type = t;
      claimedArrangementIds.add(id);
      // A % entered in the table for a trust held by a company replaces the diagram's.
      if (arr && "ownershipPercent" in arr && parentNodeShowsPercent(existingArrangementNode.parentIDs?.[0]) &&
          clean(existingArrangementNode.parentIDs?.[0]) !== "X0") {
        existingArrangementNode.percent = percentOrUndefined(arr.ownershipPercent);
        existingArrangementNode._reviewEdgePercent = normalizePercent(arr.ownershipPercent);
      }
      return;
    }

    let parentId =
      clean(arr?.sourceRefNodeId) ||
      (clean(arr?.sourceRefRowId)
        ? shareholderRowIdToTreeNodeId.get(clean(arr.sourceRefRowId)) ||
          newShareholderRowIdToMermaidId.get(clean(arr.sourceRefRowId)) ||
          ""
        : "");
    if (!parentId) return;
    const pct = parentId !== "X0" && parentNodeShowsPercent(parentId) ? arr?.ownershipPercent : "";
    const newNode = { ID: id, name: clean(arr?.name) || "…", type: t, parentIDs: [parentId], percent: percentOrUndefined(pct) };
    nodes.push(newNode);
    nodeById.set(id, newNode);
    newFlowLines.push(`${parentId} ${edgeForPercent(pct)} ${id}("${nodeLabelForMermaid(newNode, initialAHType)}")`);
  });

  const x0 = nodeById.get("X0");
  if (x0) {
    if (clean(accountHolderName)) x0.name = clean(accountHolderName);
    if (overrideX0Type) x0.type = overrideX0Type;
  }

  const ahNode = nodeById.get("X0") || {
    ID: "X0",
    name: accountHolderName,
    type: overrideX0Type || initialAHType,
  };
  const effectiveAHType = ahNode.type || initialAHType;

  // Saved nodes whose table row was deleted, with everything below them. Every direct
  // shareholder, individual and trust/foundation of a saved tree has a row; intermediate
  // companies have none and go only with their parent.
  const hasShareholderList = Array.isArray(ownership?.shareholders) || Array.isArray(ownership?.directShareholders);
  const hasArrangementList = Array.isArray(ownership?.legalArrangements?.arrangements);
  const claimedByRow = (id) =>
    shareholderByTreeNodeId.has(id) || claimedNodeIds.has(id) || claimedArrangementIds.has(id);
  const deadNodeIds = new Set(
    sourceNodes
      .filter((n) => {
        const id = clean(n?.ID);
        if (!id || id === "X0" || claimedByRow(id)) return false;
        const type = clean(n?.type);
        return type === "Individual" ||
          (hasShareholderList && clean(n?.parentIDs?.[0]) === "X0") ||
          (hasArrangementList && (type === "Trust" || type === "Foundation"));
      })
      .map((n) => clean(n.ID))
  );
  for (let grew = deadNodeIds.size > 0; grew; ) {
    grew = false;
    nodes.forEach((n) => {
      const id = clean(n?.ID);
      if (!deadNodeIds.has(id) && deadNodeIds.has(clean(n?.parentIDs?.[0]))) {
        deadNodeIds.add(id);
        grew = true;
      }
    });
  }
  const lineTouchesDeadNode = (line) => {
    const edge = String(line).match(/^\s*([\w-]+)\s*-->\s*(?:\|[^|]*\|\s*)?([\w-]+)/);
    return !!edge && (deadNodeIds.has(edge[1]) || deadNodeIds.has(edge[2]));
  };

  const flowLines = Array.isArray(snapshot?.flowLines)
    ? snapshot.flowLines
        .filter((line) => !lineTouchesDeadNode(line))
        .map((line) => {
        let nextLine = String(line || "");
        nodes.forEach((node) => {
          const nodeId = clean(node?.ID);
          if (!nodeId) return;
          nextLine = replaceNodeLabelInMermaid(
            nextLine,
            nodeId,
            nodeLabelForMermaid(node, nodeId === "X0" ? effectiveAHType : initialAHType)
          );

          const parentId =
            Array.isArray(node?.parentIDs) && node.parentIDs.length
              ? clean(node.parentIDs[0])
              : "";
          if (!parentId) return;

          const directShareholderRow = shareholderByTreeNodeId.get(nodeId);
          if (parentId === "X0" && directShareholderRow) {
            nextLine = setEdgePercentInMermaid(
              nextLine, parentId, nodeId,
              isPIC ? "" : directShareholderRow?.ownershipPercent
            );
            return;
          }

          if (!parentNodeShowsPercent(parentId)) {
            nextLine = setEdgePercentInMermaid(nextLine, parentId, nodeId, "");
          } else if ("_reviewEdgePercent" in node) {
            nextLine = setEdgePercentInMermaid(nextLine, parentId, nodeId, node._reviewEdgePercent);
          }
        });
        return nextLine;
      })
    : [];
  const base = `graph TD;X0("${nodeLabelForMermaid(ahNode, effectiveAHType)}")`;
  const seenEdges = new Set();
  const allFlowLines = [...flowLines, ...newFlowLines.filter((line) => !lineTouchesDeadNode(line))].filter((line) => {
    const edge = String(line).match(/^\s*([\w-]+)\s*-->\s*(?:\|[^|]*\|\s*)?([\w-]+)/);
    if (!edge) return true;
    const key = `${edge[1]}->${edge[2]}`;
    if (seenEdges.has(key)) return false;
    seenEdges.add(key);
    return true;
  });
  const visibleNodeIds = nodeIdsFromFlowLines(allFlowLines);
  const prunedNodes = nodes
    .filter((node) => visibleNodeIds.has(clean(node?.ID)))
    .map(removeSnapshotOnlyFields);
  const body = allFlowLines.length ? `;${allFlowLines.join(";")}` : "";
  const styleTail = `\nclassDef default fill:#BAD9CB,stroke:#BAD9CB,stroke-width:0px,color:#484848
  linkStyle default stroke-width:2px,fill:none,stroke:#B0B0B0`;

  return {
    ...snapshot,
    mermaid: `${base}${body}${styleTail}`,
    nodes: prunedNodes,
    flowLines: allFlowLines,
    initialAHType: effectiveAHType,
  };
}

export function buildReviewOwnershipApiPayload({
  ownership,
  accountHolderName,
  liveOwnershipGraph,
}) {
  const graph =
    liveOwnershipGraph ||
    buildLiveReviewOwnershipSnapshot({ ownership, accountHolderName });
  const graphNodes = Array.isArray(graph?.nodes) ? graph.nodes : [];
  const guidedRoots = Array.isArray(ownership?.assistantRoots)
    ? ownership.assistantRoots
    : [];

  const initialAHType =
    clean(graph?.initialAHType) ||
    clean(ownership?.assistantTree?.initialAHType) ||
    (clean(ownership?.mobileAssistantDraft?.preliminaryMode)
      ? mobileAssistantModeToNodeType(clean(ownership?.mobileAssistantDraft?.preliminaryMode))
      : "") ||
    fallbackFlagsToAccountHolderNodeType(ownership?.uboFlags || {}) ||
    desktopTypeToNodeType(
      clean(ownership?.desktopClientEntityType),
      clean(ownership?.desktopClientHoldingKind)
    );

  let nodes = graphNodes;
  if (!nodes.length && guidedRoots.length && initialAHType) {
    nodes = convertAssistantRootsToNodes(
      guidedRoots,
      initialAHType,
      accountHolderName
    );
  }

  return {
    initialAHType,
    nodes: attachArrangementParties(normalizeNodesForApi(nodes), ownership?.legalArrangements?.arrangements, {
      shareholders: Array.isArray(ownership?.shareholders) ? ownership.shareholders : ownership?.directShareholders,
      knownNodes: ownershipKnownNodes(ownership),
    }),
  };
}

export default function KycReviewOwnershipSection({
  sectionNo = 0,
  form,
  orgJurisdiction = null,
  onSetAt,
  changedClass,
  changedRows,
  readOnly = false,
  separateNames = false,
}) {
  const { ownershipLabel } = useOwnershipLabels(orgJurisdiction);
  const [openBuilderTrigger, setOpenBuilderTrigger] = useState(0);
  const [clientTypeModalOpen, setClientTypeModalOpen] = useState(false);
  const [pendingType, setPendingType] = useState("");
  const [pendingHoldingKind, setPendingHoldingKind] = useState("");
  // Client-type change once ownership details exist: keep the structure / start over.
  const [typeChange, setTypeChange] = useState(null);
  const [openEditTrigger, setOpenEditTrigger] = useState(0);
  // Percentages became required after a type change (note until all are entered).
  const [percentsNeeded, setPercentsNeeded] = useState(false);
  const { requestRemoval, removalDialog } = useOwnershipRemoval(form);
  const [uboModalOpen, setUboModalOpen] = useState(false);
  const [uboApiLoading, setUboApiLoading] = useState(false);
  const [uboApiError, setUboApiError] = useState("");
  const [uboApiResult, setUboApiResult] = useState(null);
  const initialOwnershipDebugLoggedRef = useRef(false);

  useEffect(() => {
    if (!clientTypeModalOpen && !uboModalOpen) return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevPaddingRight = body.style.paddingRight;
    const scrollbarW = window.innerWidth - html.clientWidth;
    if (scrollbarW > 0) body.style.paddingRight = `${scrollbarW}px`;
    html.style.overflow = "hidden";
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.paddingRight = prevPaddingRight;
    };
  }, [clientTypeModalOpen, uboModalOpen]);

  const ownership = form?.ownership || {};

  useEffect(() => {
    if (!clientTypeModalOpen) return;
    setPendingType(clean(ownership?.desktopClientEntityType));
    setPendingHoldingKind(clean(ownership?.desktopClientHoldingKind));
    // eslint-disable-next-line
  }, [clientTypeModalOpen]);

  function applyClientTypeChange() {
    const newType = pendingType;
    const newHoldingKind = newType === "holding" ? pendingHoldingKind : "";
    const isNewPIC = newType === "pic" || (newType === "holding" && newHoldingKind === "passive");
    const isCurrentPIC = !!ownership?.uboFlags?.pctNotMeaningful;

    const currentType = clean(ownership?.desktopClientEntityType);
    const currentHoldingKind = clean(ownership?.desktopClientHoldingKind);
    if (newType === currentType && newHoldingKind === currentHoldingKind) {
      setClientTypeModalOpen(false);
      return;
    }

    setClientTypeModalOpen(false);
    const hasDetails =
      [ownership?.shareholders, ownership?.ubos, ownership?.legalArrangements?.arrangements]
        .some((list) => (Array.isArray(list) ? list : []).some((row) => clean(row?.name))) ||
      (Array.isArray(ownership?.assistantTree?.nodes) && ownership.assistantTree.nodes.length > 1);
    const keepable = ["operating", "holding", "pic"];
    // Between operating company / holding / PIC no party is removed: the structure is kept, no
    // question. Changes that remove parties (trust, foundation, quoted) ask first.
    if (keepable.includes(newType) && (!hasDetails || keepable.includes(currentType))) {
      keepClientTypeChange(newType, newHoldingKind, isNewPIC, isCurrentPIC);
      return;
    }
    setTypeChange({ newType, newHoldingKind, isNewPIC, isCurrentPIC, canKeep: false });
  }

  // Only the client's type changes; rows and structure stay (UBOs are re-checked automatically).
  function keepClientTypeChange(newType, newHoldingKind, isNewPIC, isCurrentPIC) {
    setPercentsNeeded(isCurrentPIC && !isNewPIC);
    onSetAt?.(["ownership", "desktopClientEntityType"], newType);
    onSetAt?.(["ownership", "desktopClientHoldingKind"], newHoldingKind);
    if (isNewPIC !== isCurrentPIC) {
      const nextFlags = { ...(ownership?.uboFlags || {}), pctNotMeaningful: isNewPIC };
      onSetAt?.(["ownership", "uboFlags"], nextFlags);
    }
  }

  // The ownership details and what is attached to the parties are removed, then the type changes.
  function removeAndChangeClientType({ newType, newHoldingKind }) {
    requestRemoval(allOwnershipParties(ownership), {
      mode: "delete",
      title: "Remove the ownership details?",
      intro: "The ownership details will be removed, with the documents of these parties:",
      applyLabel: "Remove and change",
      backLabel: "Cancel",
      onConfirmed: ({ signatories }) => {
        onSetAt?.(["ownership"], {
          ...ownership,
          shareholders: [],
          directShareholders: [],
          ubos: [],
          controllingPersonsNatural: [],
          legalArrangements: { arrangements: [] },
          controllingPersonsLegalArrangements: { arrangements: [] },
          assistantTree: null,
          assistantRoots: [],
          mobileAssistantDraft: null,
          uboAssessment: null,
          qualifiedUbos: [],
          signatories,
          desktopClientEntityType: newType,
          desktopClientHoldingKind: newHoldingKind,
          uboFlags: {
            ...(ownership?.uboFlags || {}),
            pctNotMeaningful: newType === "pic" || (newType === "holding" && newHoldingKind === "passive"),
            quotedCompany: newType === "quoted",
            accountHolderTrust: newType === "trust",
            accountHolderFoundation: newType === "foundation",
            hasOtherControl: false,
          },
        });
      },
    });
  }

  const accountHolderName = clean(form?.customer?.name) || "Account holder";
  const typeLabel = ownershipLabel(accountHolderTypeLabel(ownership));
  const methodLabel = completionMethodLabel(ownership);
  const liveOwnershipGraph = React.useMemo(
    () => buildLiveReviewOwnershipSnapshot({ ownership, accountHolderName }),
    [accountHolderName, ownership]
  );
  const hasOwnershipDiagram = !!liveOwnershipGraph?.mermaid;

  const uboApiPayload = React.useMemo(
    () =>
      buildReviewOwnershipApiPayload({
        ownership,
        accountHolderName,
        liveOwnershipGraph,
      }),
    [accountHolderName, ownership, liveOwnershipGraph]
  );

  useEffect(() => {
    if (initialOwnershipDebugLoggedRef.current) return;

    const hasOwnershipData =
      !!liveOwnershipGraph?.mermaid ||
      Array.isArray(ownership?.shareholders) ||
      Array.isArray(ownership?.ubos) ||
      Array.isArray(ownership?.legalArrangements?.arrangements);

    if (!hasOwnershipData) return;

    initialOwnershipDebugLoggedRef.current = true;
    const copyDebugPayload = buildCopyDebugPayload({
      stage: "initial_load",
      accountHolderName,
      ownership,
      liveOwnershipGraph,
      uboApiPayload,
    });

    logCopyDebugPayload(copyDebugPayload);

  }, [accountHolderName, liveOwnershipGraph, ownership, uboApiPayload]);

  const uboApiRows = React.useMemo(() => {
    const ids = Array.isArray(uboApiResult?.ubos) ? uboApiResult.ubos : [];
    return ids.map((id) => {
      const node = findNodeByApiId(uboApiPayload.nodes, id);
      return {
        id: clean(id),
        name: getNodeDisplayName(node, id),
        type: clean(node?.type) || "Individual",
      };
    });
  }, [uboApiPayload.nodes, uboApiResult]);

  const uboDirectShareholderRows = React.useMemo(() => {
    const ids = Array.isArray(uboApiResult?.directShareholders)
      ? uboApiResult.directShareholders
      : [];
    return ids.map((id) => {
      const node = findNodeByApiId(uboApiPayload.nodes, id);
      return {
        id: clean(id),
        name: getNodeDisplayName(node, id),
        type: clean(node?.type) || "Individual",
      };
    });
  }, [uboApiPayload.nodes, uboApiResult]);

  const uboLegalArrangementRows = React.useMemo(() => {
    const ids = Array.isArray(uboApiResult?.legalArrangements)
      ? uboApiResult.legalArrangements
      : [];
    return ids.map((id) => {
      const node = findNodeByApiId(uboApiPayload.nodes, id);
      const arrangement = findLegalArrangementForDetectedNode({
        ownership,
        detectedId: id,
        node,
      });
      return {
        id: clean(id),
        name: getNodeDisplayName(node, id),
        type: clean(node?.type) || "Legal arrangement",
        parties: legalArrangementParties(arrangement),
      };
    });
  }, [ownership, uboApiPayload.nodes, uboApiResult]);

  const beneficialOwnerCount =
    uboDirectShareholderRows.length + uboApiRows.length;

  function handleReviewOwnershipSetAt(path, value) {
    const isOwnershipUpdate = Array.isArray(path) && path[0] === "ownership";

    if (isOwnershipUpdate) {
      const nextOwnership = previewOwnershipAfterSetAt(ownership, path, value);
      const nextLiveGraph = buildLiveReviewOwnershipSnapshot({
        ownership: nextOwnership,
        accountHolderName,
      });
      const nextApiPayload = buildReviewOwnershipApiPayload({
        ownership: nextOwnership,
        accountHolderName,
        liveOwnershipGraph: nextLiveGraph,
      });

      const copyDebugPayload = buildCopyDebugPayload({
        stage: "manual_update",
        accountHolderName,
        ownership: nextOwnership,
        liveOwnershipGraph: nextLiveGraph,
        uboApiPayload: nextApiPayload,
        updatePath: path,
        updatedValue: value,
      });

      logCopyDebugPayload(copyDebugPayload);

    }

    onSetAt?.(path, value);
  }

  async function openUboIdentificationModal() {
    setUboModalOpen(true);
    setUboApiLoading(true);
    setUboApiError("");
    setUboApiResult(null);

    try {
      if (!uboApiPayload.initialAHType || !uboApiPayload.nodes.length) {
        throw new Error(
          "No ownership tree is available for UBO identification."
        );
      }
      const copyDebugPayload = buildCopyDebugPayload({
        stage: "ubo_identification_click",
        accountHolderName,
        ownership,
        liveOwnershipGraph,
        uboApiPayload,
      });

      logCopyDebugPayload(copyDebugPayload);

      const dataSentToApi = {
        initialAHType: uboApiPayload.initialAHType,
        nodes: uboApiPayload.nodes,
      };
      const result = await assessUbosFromTree(
        dataSentToApi.initialAHType,
        dataSentToApi.nodes,
        orgJurisdiction
      );
      setUboApiResult(result || {});
      const assessedOwnership = applyUboAssessmentResultToOwnership({
        ownership,
        apiPayload: uboApiPayload,
        apiResult: result || {},
        signature: "",
      });

      onSetAt?.(["ownership"], assessedOwnership);
    } catch (e) {
      setUboApiError(e?.message || "UBO identification failed.");
    } finally {
      setUboApiLoading(false);
    }
  }

  return (
    <div className="card kycCard mb-3 kycReviewOwnershipSection">
      <div className="kycSectionHead">
        <h5 className="kycSectionTitle mb-0">
          {sectionNo}
          {" | Ownership & Control"}
        </h5>
      </div>

      <div className="kycSectionBody">
        <div className="kycReviewOwnershipStack">
          <div className="kycReviewOwnershipGroup">
            <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
              <div className="kycReviewOwnershipSubhead">
                Entity Type of the account holder
              </div>
              <div className="kycReviewOwnershipActionBtns">
                <button
                  type="button"
                  className="btn btn-sm btn-ownership-assistant"
                  onClick={openUboIdentificationModal}
                  disabled={uboApiLoading}
                >
                  <i className="bi bi-person-check me-2" aria-hidden="true" />
                  {uboApiLoading ? "Identifying..." : "UBOs Identification"}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-ownership-assistant"
                  onClick={() => setOpenBuilderTrigger((t) => t + 1)}
                >
                  <i className="bi bi-magic me-2" aria-hidden="true" />
                  Open ownership assistant
                </button>
              </div>
            </div>
            <div className="kycReviewOwnershipTile kycReviewOwnershipEntityType">
              <div className="kycInfoViewText kycPrelimText">
                {accountHolderName} is {articleFor(typeLabel)}{" "}
                <span className="kycPrelimHighlight">{typeLabel}</span>. The KYC
                was completed via{" "}
                <span className="kycPrelimHighlight">{methodLabel}</span>.
              </div>
              <button
                type="button"
                className="kycSigAddBtn kycReviewChangeClientTypeBtn"
                onClick={() => setClientTypeModalOpen(true)}
                disabled={readOnly}
              >
                Change Client Type
              </button>
            </div>
          </div>

          {hasOwnershipDiagram ? (
            <div className="kycReviewOwnershipGroup">
              <div className="kycReviewOwnershipSubhead">Ownership diagram</div>
              <div className="kycReviewOwnershipTile kycReviewOwnershipDiagram" style={{ position: "relative" }}>
                <OwnershipTreeUaeReadOnly
                  snapshot={liveOwnershipGraph}
                  edgeLabelBg="#fdfdfd"
                />
                {!readOnly && liveOwnershipGraph?.nodes?.length > 1 ? (
                  <button
                    type="button"
                    className="kycOwnershipDiagramEditBtn"
                    onClick={() => setOpenEditTrigger((t) => t + 1)}
                    title="Edit the ownership structure in the visual builder"
                  >
                    <i className="bi bi-pencil" aria-hidden="true" /> Edit
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          <KycSectionOwnership
            orgJurisdiction={orgJurisdiction}
            form={form}
            separateNames={separateNames}
            mode="client"
            onSetAt={handleReviewOwnershipSetAt}
            changedClass={changedClass}
            changedRows={changedRows}
            readOnly={readOnly}
            hideOwnershipAssistantIntro
            hidePreliminaryInformation
            hideAssistantSnapshot
            showSignatories={false}
            embedded
            openAssistantTrigger={openBuilderTrigger}
            openEditTrigger={openEditTrigger}
            shareholdersNote={
              percentsNeeded && !ownership?.uboFlags?.pctNotMeaningful &&
              (Array.isArray(ownership?.shareholders) ? ownership.shareholders : [])
                .some((row) => clean(row?.name) && !clean(row?.ownershipPercent))
                ? "Please enter each shareholder's percentage." : ""
            }
            assistantSnapshotOverride={liveOwnershipGraph}
            noModeReset
          />

          {removalDialog}
          <OwnershipChoiceDialog
            open={!!typeChange}
            title={`Change the client type to ${typeChange?.newType === "holding" && typeChange?.newHoldingKind
              ? `${typeChange.newHoldingKind === "active" ? "an active" : "a passive"} holding`
              : ownershipLabel((CLIENT_TYPE_OPTIONS.find((o) => o.value === typeChange?.newType)?.title || "this type").replace(/^./, (c) => c.toLowerCase()))}?`}
            hint={typeChange?.canKeep ? "Choose what happens to the ownership details already entered." : ""}
            message={typeChange?.canKeep ? "" : "The ownership details entered so far (shareholders, controlling persons, trusts and the diagram) will be removed."}
            options={typeChange?.canKeep ? [
              {
                value: "keep",
                label: "Keep the ownership structure",
                hint: "Only the client type changes. Shareholders and the structure below stay; UBOs are identified again with the new rules.",
                // From a PIC / passive holding to an operating company / active holding.
                note: typeChange?.isCurrentPIC && !typeChange?.isNewPIC
                  ? "Shareholder percentages are required for this client type and will need to be entered." : "",
                confirmLabel: "Change type",
              },
              { value: "start", label: "Start over", hint: "Remove the current ownership details.", confirmLabel: "Remove and change", danger: true },
            ] : []}
            confirmLabel="Remove and change"
            danger
            onCancel={() => setTypeChange(null)}
            onConfirm={(choice) => {
              const t = typeChange;
              setTypeChange(null);
              if (choice === "keep") keepClientTypeChange(t.newType, t.newHoldingKind, t.isNewPIC, t.isCurrentPIC);
              else removeAndChangeClientType(t);
            }}
          />

          {clientTypeModalOpen ? (
            <div
              className="kycReviewClientTypeModalBackdrop"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) setClientTypeModalOpen(false);
              }}
            >
              <div
                className="kycReviewClientTypeModal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="kycReviewClientTypeModalTitle"
              >
                <div className="kycReviewClientTypeModalHead">
                  <div>
                    <div
                      id="kycReviewClientTypeModalTitle"
                      className="kycReviewClientTypeModalTitle"
                    >
                      Change Client Type
                    </div>
                    <div className="kycReviewClientTypeModalHint">
                      Select the client type to use for this ownership review.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="kycReviewClientTypeModalClose"
                    onClick={() => setClientTypeModalOpen(false)}
                    aria-label="Close"
                  >
                    <i className="bi bi-x-lg" aria-hidden="true" />
                  </button>
                </div>

                <div className="kycReviewClientTypeGrid">
                  {CLIENT_TYPE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`kycReviewClientTypeChoice${pendingType === option.value ? " is-selected" : ""}`}
                      onClick={() => {
                        setPendingType(option.value);
                        if (option.value !== "holding") setPendingHoldingKind("");
                      }}
                    >
                      <span className="kycReviewClientTypeChoiceIcon">
                        <img src={option.iconSrc} alt="" aria-hidden="true" />
                      </span>
                      <span className="kycReviewClientTypeChoiceCopy">
                        <span className="kycReviewClientTypeChoiceTitle">
                          {ownershipLabel(option.title)}
                        </span>
                        <span className="kycReviewClientTypeChoiceHint">
                          {option.hint}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>

                {pendingType === "holding" ? (
                  <div className="kycReviewHoldingKindWrap">
                    <div className="kycReviewHoldingKindHead">
                      <strong>Holding type</strong>
                      <p>Specify whether the holding company is active or passive.</p>
                    </div>
                    <div className="kycReviewHoldingKindGrid">
                      {[
                        { value: "active", label: "Active holding", hint: "Holds directly or indirectly a majority stake in one or more operating companies. Main purpose is NOT the administration of third-party assets." },
                        { value: "passive", label: "Passive holding", hint: "Mainly combines and manages assets of a family or a defined group. Often used to enable dividend distributions." },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          className={pendingHoldingKind === opt.value ? "is-active" : ""}
                          onClick={() => setPendingHoldingKind(opt.value)}
                        >
                          <strong>{opt.label}</strong>
                          <span>{opt.hint}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="kycReviewClientTypeModalFooter">
                  <button
                    type="button"
                    className="kycReviewClientTypeModalCancel"
                    onClick={() => setClientTypeModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="kycReviewClientTypeModalApply"
                    disabled={
                      !pendingType ||
                      (pendingType === "holding" && !pendingHoldingKind) ||
                      (pendingType === clean(ownership?.desktopClientEntityType) &&
                        (pendingType !== "holding" ||
                          pendingHoldingKind === clean(ownership?.desktopClientHoldingKind)))
                    }
                    onClick={applyClientTypeChange}
                  >
                    Confirm change
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <div
              className={`kycReviewClientTypeModalBackdrop${uboModalOpen ? " isOpen" : ""}`}
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) setUboModalOpen(false);
              }}
            >
              <motion.div
                className="kycReviewUboModal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="kycReviewUboModalTitle"
                layout
                transition={{ layout: { duration: 0.22, ease: "easeOut" } }}
              >
                <div className="kycReviewClientTypeModalHead">
                  <div>
                    <div
                      id="kycReviewUboModalTitle"
                      className="kycReviewClientTypeModalTitle"
                    >
                      UBOs Identification
                    </div>
                    <div className="kycReviewClientTypeModalHint">
                      {Array.isArray(uboApiResult?.forms) ? "CDB 2020 forms required for the current ownership structure, pre-filled with the persons to declare." : "UBOs meeting the identification threshold based on the current ownership structure."}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="kycReviewClientTypeModalClose"
                    onClick={() => setUboModalOpen(false)}
                    aria-label="Close"
                  >
                    <i className="bi bi-x-lg" aria-hidden="true" />
                  </button>
                </div>

                {uboApiLoading ? (
                  <div className="kycReviewUboLoading">
                    <span className="spinner-border spinner-border-sm" />
                    <span>Identifying UBOs...</span>
                  </div>
                ) : uboApiError ? (
                  <div className="alert alert-warning mb-0">{uboApiError}</div>
                ) : (
                  <div className="kycReviewUboModalBody">
                    <UboIdentificationNotice result={uboApiResult} nodes={uboApiPayload.nodes} />
                    {Array.isArray(uboApiResult?.forms) ? (
                      <KycCdbFormsPanel result={uboApiResult} />
                    ) : (
                    <>
                    <div className="kycReviewUboSummary">
                      {uboApiResult?.meta?.reviewRequired
                        ? beneficialOwnerCount
                          ? `${beneficialOwnerCount} beneficial owner${beneficialOwnerCount === 1 ? "" : "s"} identified so far — review incomplete`
                          : "Beneficial owners not yet confirmed"
                        : `${beneficialOwnerCount} beneficial owner${beneficialOwnerCount === 1 ? "" : "s"} meeting threshold`}
                    </div>

                    {uboDirectShareholderRows.length ? (
                      <div className="kycReviewUboList">
                        <div className="kycReviewUboLegalTitle">
                          Direct shareholders
                        </div>
                        {uboDirectShareholderRows.map((row) => (
                          <div key={row.id} className="kycReviewUboRow">
                            <div className="kycReviewUboAvatar">
                              <img src={iconIndividual} alt="" aria-hidden="true" />
                            </div>
                            <div className="kycReviewUboRowMain">
                              <div className="kycReviewUboName">{row.name}</div>
                              <div className="kycReviewUboMeta">
                                {row.type}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {uboApiRows.length ? (
                      <div className="kycReviewUboList">
                        <div className="kycReviewUboLegalTitle">
                          Indirect UBOs
                        </div>
                        {uboApiRows.map((row) => (
                          <div key={row.id} className="kycReviewUboRow">
                            <div className="kycReviewUboAvatar">
                              <img src={iconIndividual} alt="" aria-hidden="true" />
                            </div>
                            <div className="kycReviewUboRowMain">
                              <div className="kycReviewUboName">{row.name}</div>
                              <div className="kycReviewUboMeta">
                                {row.type}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {!beneficialOwnerCount ? (
                      <div className="kycReviewUboEmpty">
                        {uboApiResult?.meta?.reviewRequired
                          ? "The assessment is incomplete. This does not mean there are no beneficial owners. Review the items above to complete identification."
                          : "No beneficial owner meeting threshold was returned for this ownership structure."}
                      </div>
                    ) : null}

                    {uboLegalArrangementRows.length ? (
                      <div className="kycReviewUboLegalBlock">
                        <div className="kycReviewUboLegalTitle">
                          Legal arrangements
                        </div>
                        <div className="kycReviewUboLegalArrangementRows">
                          {uboLegalArrangementRows.map((row) => (
                            <div
                              key={row.id}
                              className="kycReviewUboLegalArrangementCard"
                            >
                              <div className="kycReviewUboLegalArrangementLeft">
                                <div className="kycReviewUboLegalArrangementIcon">
                                  <img
                                    src={
                                      row.type === "Foundation"
                                        ? iconFoundation
                                        : iconTrust
                                    }
                                    alt=""
                                    aria-hidden="true"
                                  />
                                </div>
                                <div className="kycReviewUboLegalArrangementIdentity">
                                  <div className="kycReviewUboName">
                                    {row.name}
                                  </div>
                                  <div className="kycReviewUboMeta">
                                    {row.type}
                                  </div>
                                </div>
                              </div>
                              <div className="kycReviewUboLegalParties">
                                {row.parties.length ? (
                                  row.parties.map((party) => (
                                    <div
                                      key={party.id}
                                      className="kycReviewUboLegalPartyRow"
                                    >
                                      <div className="kycReviewUboAvatar">
                                        <img
                                          src={party.icon}
                                          alt=""
                                          aria-hidden="true"
                                        />
                                      </div>
                                      <div className="kycReviewUboRowMain">
                                        <div className="kycReviewUboName">
                                          {party.name}
                                        </div>
                                        <div className="kycReviewUboMeta">
                                          {party.meta}
                                        </div>
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="kycReviewUboLegalEmptyParties">
                                    No related individuals recorded.
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    </>
                  )}
                  </div>
                )}

              </motion.div>
            </div>
        </div>
      </div>
    </div>
  );
}
